import React, { FC, useMemo, useState } from "react";
import VODAdminLayout from "../../../layouts/admin/VODAdminLayout";
import { NavLink, useParams } from "react-router";
import {
    useExportUpdateVodContentGroup,
    useGetContentGroup,
    useGetContentGroupContent,
    useGetContentSubGroups,
    useGetFpbSummary,
} from "../../../services/vod/Query";
import {
    AudioType,
    Content,
    ContentGroup,
    ContentGroupTypeLabels,
    Image,
    ImageOrientationLabels,
    ImagePurposeLabels,
    ImageSizeLabels,
    PreviewVideoAsset,
    VideoType,
} from "../../../models/vod/Models";
import VODContentSubGroupTable from "./components/assets/VODContentSubGroupTable";
import VODContentGroupContentTable from "./components/assets/VODContentGroupContentTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCalendarDays,
    faChevronRight,
    faFilm,
    faGavel,
    faImages,
    faLink,
    faPenToSquare,
    faRectangleList,
    faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import Card from "../../../components/themed/Card";
import { toCountryName } from "../../../const";
import dayjs from "dayjs";
import DashboardImage from "../../../components/DashboardImage";
import ContentGroupModal from "./components/form/ContentGroupModal";
import ExportModal from "./components/modal/ExportModal";
import FpbSummaryModal from "./components/form/FpbSummaryModal";
import FileUpload from "./components/FileUpload";
import PreviewVideoAssetFormModal from "./components/form/PreviewVideoAssetFormModal";
import OfferWindowsForm from "./components/form/OfferWindowsForm";

const VODContentGroupView: FC = () => {
    const [ currentContentGroupId, setCurrentContentId ] = useState<string | null | undefined>();

    const {
        boxSetAmsProviderId,
        boxSetBaseContentGroupId,
        seriesAmsProviderId,
        seriesBaseContentGroupId,
        seasonAmsProviderId,
        seasonBaseContentGroupId,
    } = useParams();

    const { data: boxSetContentGroup } =
        useGetContentGroup(boxSetAmsProviderId ?? "", boxSetBaseContentGroupId ?? "", !!boxSetAmsProviderId && !!boxSetBaseContentGroupId);

    const { data: seriesContentGroup } =
        useGetContentGroup(seriesAmsProviderId ?? "", seriesBaseContentGroupId ?? "", !!seriesAmsProviderId && !!seriesBaseContentGroupId);

    const { data: seasonContentGroup } =
        useGetContentGroup(seasonAmsProviderId ?? "", seasonBaseContentGroupId ?? "", !!seasonAmsProviderId && !!seasonBaseContentGroupId);

    const exportUpdateVodContentGroup = useExportUpdateVodContentGroup();

    const [
        contentGroup,
        amsProviderId,
        baseContentGroupId,
    ] = useMemo(() => [
        seasonContentGroup ? seasonContentGroup : (seriesContentGroup ? seriesContentGroup : (boxSetContentGroup ? boxSetContentGroup : undefined)),
        seasonAmsProviderId ? seasonAmsProviderId : (seriesAmsProviderId ? seriesAmsProviderId : (boxSetAmsProviderId ? boxSetAmsProviderId : undefined)),
        seasonBaseContentGroupId ? seasonBaseContentGroupId : (seriesBaseContentGroupId ? seriesBaseContentGroupId : (boxSetBaseContentGroupId ? boxSetBaseContentGroupId : undefined)),
    ], [
        boxSetAmsProviderId,
        boxSetBaseContentGroupId,
        boxSetContentGroup,
        seasonAmsProviderId,
        seasonBaseContentGroupId,
        seasonContentGroup,
        seriesAmsProviderId,
        seriesBaseContentGroupId,
        seriesContentGroup,
    ]);

    const { data: contentSubGroups } =
        useGetContentSubGroups(amsProviderId ?? "", baseContentGroupId ?? "");

    const { data: contentGroupContent } =
        useGetContentGroupContent(amsProviderId ?? "", baseContentGroupId ?? "");

    const { data: fpbSummary } = useGetFpbSummary(contentGroup && contentGroup.id ? contentGroup.id : "");

    const [ openEditFpbSummaryModal, setOpenEditFpbSummaryModal ] = useState(false);
    const [ openPreviewVideoAssetEditModal,  setPreviewVideoAssetEditModal ] = useState(false);
    const [ openOfferWindowsEditModal,  setOpenOfferWindowsEditModal ] = useState(false);

    const openModal = (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        setOpenModal: (openModal: boolean) => void,
    ) => {
        e.stopPropagation();
        setOpenModal(true);
    };

    const closeModal = (
        setOpenModal: (openModal: boolean) => void
    ) => {
        setOpenModal(false);
    };

    const renderNavigation = (
        boxSetContentGroup: ContentGroup | undefined,
        seriesContentGroup: ContentGroup | undefined,
        seasonContentGroup: ContentGroup | undefined,
    ) => {
        const boxSetNavLink = boxSetContentGroup ? `/box-set-content-group/${boxSetContentGroup.id}` : "";
        const seriesNavLink = seriesContentGroup ? `/series-content-group/${seriesContentGroup.id}` : "";
        const seasonNavLink = seasonContentGroup ? `/season-content-group/${seasonContentGroup.id}` : "";
        return (
            <>
                <Card className="px-6 py-4">
                    <div className={`flex justify-between`}>
                        <div className="flex flex-row flex-wrap">
                    {boxSetContentGroup && boxSetNavLink && (
                        <>
                            <NavLink
                                to={`/dashboard/vod/assets/box-set-content-group`}
                            >
                                VOD Assets
                            </NavLink>
                            <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                            <NavLink
                                to={`/dashboard/vod/assets${boxSetNavLink}`}
                            >
                                {boxSetContentGroup.title}
                            </NavLink>
                        </>
                    )}
                    {seriesContentGroup && seriesNavLink && (
                        <>
                            {boxSetContentGroup ? (
                                <>
                                    <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                    <NavLink
                                        to={`/dashboard/vod/assets${boxSetNavLink}${seriesNavLink}`}
                                    >
                                        {seriesContentGroup.title}
                                    </NavLink>
                                </>
                            ) : (
                                <>
                                    <NavLink
                                        to={`/dashboard/vod/assets/series-content-group`}
                                    >
                                        VOD Assets
                                    </NavLink>
                                    <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                                    <NavLink
                                        to={`/dashboard/vod/assets${seriesNavLink}`}
                                    >
                                        {seriesContentGroup.title}
                                    </NavLink>
                                </>
                            )}
                        </>
                    )}
                    {seasonContentGroup && seasonNavLink && (
                        <>
                            <FontAwesomeIcon icon={faChevronRight} className="py-1 mx-4" />
                            <NavLink
                                to={`/dashboard/vod/assets${boxSetNavLink}${seriesNavLink}${seasonNavLink}`}
                            >
                                {seasonContentGroup.title}
                            </NavLink>
                        </>
                    )}
                        </div>
                        <div className={"flex gap-2"}>
                            <button
                                data-tooltip-content="Edit FPB Summary"
                                data-tooltip-id="global-tooltip"
                                onClick={(e) => openModal(e, setOpenEditFpbSummaryModal)}
                            >
                                <FontAwesomeIcon icon={faGavel} size="1x" className="text-brand-300 ml-4"/>
                            </button>
                            <button
                                data-tooltip-content="Synamedia Export"
                                data-tooltip-id="global-tooltip"
                                command="show-modal" commandfor="export-update-vod-content-group-view"
                                onClick={() => {
                                    setCurrentContentId(contentGroup?.id);
                                }}
                            >
                                <FontAwesomeIcon icon={faUpRightFromSquare} size="1x" className="text-brand-300"/>
                            </button>
                        </div>
                    </div>
                </Card>
                {
                    contentGroup && contentGroup.id &&
                    <ExportModal
                        mutateFunc={exportUpdateVodContentGroup.mutate}
                        title ={`${contentGroup.id} - ${contentGroup.title}`}
                        id={currentContentGroupId ?? ""}
                        modalId={"export-update-vod-content-group-view"}
                    />
                }
                {
                    fpbSummary &&
                    <FpbSummaryModal
                        openModal = {openEditFpbSummaryModal}
                        onClose = {() => closeModal(setOpenEditFpbSummaryModal)}
                        key = {fpbSummary.itemId}
                        modalId = {"edit-modal-fpb"}
                        content = {fpbSummary}
                        onlyCertificateFields
                    />
                }
            </>
        );
    };

    const badge = (item: string, colourVariant: string = "brand") => {
        return (
            <div key={item.toLowerCase().replace(/^0-9a-z/, "_")}
                 className={`py-1 px-2 text-xs text-${colourVariant}-900 bg-${colourVariant}-300 dark:text-white dark:bg-${colourVariant}-500 font-bold rounded-full`}
            >
                {item}
            </div>
        );
    };
    const badgeList = (items: string[] | null | undefined, colourVariant: string = "brand", emptyListValue: string = "None") => {
        return (
            <div className="flex flex-row flex-wrap gap-1">
                {
                    items?.length
                    ? items.filter(item => !!item).map(item => badge(item, colourVariant))
                    : (emptyListValue && badge(emptyListValue, "red"))
                }
            </div>
        );
    };

    const renderMainMetadata = (
        seriesContentGroup: ContentGroup | undefined,
        seasonContentGroup: ContentGroup | undefined,
    ) => {
        const contentGroup = seasonContentGroup ?? seriesContentGroup;

        if (!contentGroup) return <></>;

        return (
            <>
                <Card className="px-8 py-4">
                    <div className="pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white flex flex-row items-center justify-between">
                        <div className="text-lg flex flex-row flex-wrap items-center">
                            <span className="font-bold mr-6">
                                <FontAwesomeIcon icon={faRectangleList} className="mx-4"/>
                                {seriesContentGroup && seriesContentGroup.title}
                                {seasonContentGroup && <FontAwesomeIcon icon={faChevronRight} className="mx-4"/>}
                                {seasonContentGroup && seasonContentGroup.title}
                            </span>
                            <span className="mr-4 text-gray-500 text-sm">
                                {
                                    (() => {
                                        if (!contentGroup.contentGroupType)
                                            return "";
                                        const contentGroupLabel = ContentGroupTypeLabels[contentGroup.contentGroupType];

                                        if (contentGroupLabel !== "Season")
                                            return contentGroupLabel;
                                        return `${contentGroupLabel} ${contentGroup.sequenceNumber}`;
                                    })()
                                }
                            </span>
                        </div>
                        <button
                            data-tooltip-content= {`Edit ${contentGroup ? 
                                `${contentGroup.contentGroupType?.charAt(0)}${contentGroup.contentGroupType?.substring(1).toLowerCase()}` : "Content Group"} Main Metadata`}
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="edit-vod-content-group-main-metadata"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                        </button>
                    </div>
                    <div className="flex flex-row flex-wrap mb-4">
                        <div className="flex flex-col flex-wrap gap-1 w-[50%]">
                            <label className="font-bold">Categories</label>
                            {badgeList(contentGroup.classification?.categories)}
                        </div>
                        <div className="flex flex-col flex-wrap gap-1 w-[50%]">
                            <label className="font-bold">Genres</label>
                            {badgeList(contentGroup.classification?.genres)}
                        </div>
                    </div>
                    <div className="mb-4">
                        {
                            contentGroup.summaries?.longSynopsis &&
                            <div>
                                <label className="font-bold">Long Synopsis</label>
                                <p className="text-sm text-justify">{contentGroup.summaries.longSynopsis}</p>
                            </div>
                        }
                        {
                            contentGroup.summaries?.mediumSynopsis &&
                            <div>
                                <label className="font-bold">Medium Synopsis</label>
                                <p className="text-sm text-justify">{contentGroup.summaries.mediumSynopsis}</p>
                            </div>
                        }
                        {
                            contentGroup.summaries?.shortSynopsis &&
                            <div>
                                <label className="font-bold">Short Synopsis</label>
                                <p className="text-sm text-justify">{contentGroup.summaries.shortSynopsis}</p>
                            </div>
                        }
                    </div>
                    <div className="flex flex-col gap-1 flex-wrap mb-4">
                        <label className="font-bold">Keywords</label>
                        {badgeList(contentGroup.classification?.keywords)}
                    </div>
                    <div className="flex flex-row flex-wrap justify-between items-end mb-4">
                        <div>
                            { !!contentGroup.releaseInfo?.yearOfRelease && badge(String(contentGroup.releaseInfo.yearOfRelease), "gray") }
                        </div>
                        <div className="flex flex-row flex-wrap">
                        {
                            !!contentGroup.releaseInfo?.countriesOfOrigin?.length &&
                            badgeList(contentGroup.releaseInfo.countriesOfOrigin.map(countryCode => toCountryName(countryCode)), "gray")
                        }
                        </div>
                        <div className="flex flex-col">
                        {
                            contentGroup.parentalRatings?.map(parentalRating =>
                                <div key={`${parentalRating.region}_${parentalRating.system}`}>
                                    <label className="font-bold">
                                        <span className="font-bold mr-4">{parentalRating.system?.toUpperCase()}</span>
                                        <span className="text-sm text-gray-500">{parentalRating.region?.toUpperCase()}</span>
                                    </label>
                                    <div className="flex flex-row">
                                        <div className="mr-2">
                                            {parentalRating.label ? badge(parentalRating.label) : badge("Not Specified", "red")}
                                        </div>
                                        {!!parentalRating.advisories?.length && badgeList(parentalRating.advisories, "gray")}
                                    </div>
                                </div>
                            )
                        }
                        </div>
                    </div>
                    <div className="flex flex-col mb-4">
                    {
                        !!contentGroup.castAndCrew?.actors?.filter(t => !!t)?.length &&
                        <div>
                            <label className="font-bold">Actors</label>
                            <p className="text-sm">
                            {contentGroup.castAndCrew.actors.map(actor => actor.split(",").reverse().join(" ")).join(", ")}
                            </p>
                        </div>
                    }
                    {
                        !!contentGroup.castAndCrew?.directors?.filter(t => !!t)?.length &&
                        <div>
                            <label className="font-bold">Directors</label>
                            <p className="text-sm">
                            {contentGroup.castAndCrew.directors.map(director => director.split(",").reverse().join(" ")).join(", ")}
                            </p>
                        </div>
                    }
                    {
                        !!contentGroup.castAndCrew?.producers?.filter(t => !!t)?.length &&
                        <div>
                            <label className="font-bold">Producers</label>
                            <p className="text-sm">
                            {contentGroup.castAndCrew.producers.map(producer => producer.split(",").reverse().join(" ")).join(", ")}
                            </p>
                        </div>
                    }
                    </div>
                </Card>
                {
                    contentGroup &&
                    <ContentGroupModal contentGroup={contentGroup} modalId={"edit-vod-content-group-main-metadata"}/>
                }
            </>
        );
    };

    const renderReferences = (contentGroup: ContentGroup) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faLink} className="mx-4"/>
                        References
                    </span>
                </h2>
                <div className="grid grid-cols-2 grid-rows-[auto] gap-x-4 gap-y-2 items-center">
                    <div className="text-right font-bold">Id</div>
                    <div className="text-sm">{contentGroup.id}</div>
                    <div className="text-right font-bold">Provider Asset Id</div>
                    <div className="text-sm">{contentGroup.providerAssetId}</div>
                    <div className="text-right font-bold">Aggregator Id</div>
                    <div className="text-sm">{contentGroup.providerId}</div>
                    <div className="text-right font-bold">Aggregator Name</div>
                    <div className="text-sm">{contentGroup.providerName}</div>
                    <div className="text-right font-bold">Content Partner Id</div>
                    <div className="text-sm">{contentGroup.contentPartnerId}</div>
                    <div className="text-right font-bold">Content Partner Name</div>
                    <div className="text-sm">{contentGroup.contentPartnerName}</div>
                    <div className="text-right font-bold">Product</div>
                    <div className="text-sm">{contentGroup.product}</div>
                    <div className="text-right font-bold">Created</div>
                    <div className="text-sm">{dayjs(contentGroup.createdDateTime).format("DD MMM YYYY [@] HH:mm")}</div>
                    <div className="text-right font-bold">Last Modified</div>
                    <div className="text-sm">{dayjs(contentGroup.lastModifiedDateTime).format("DD MMM YYYY [@] HH:mm")}</div>
                    <div className="text-right font-bold">Version</div>
                    <div className="text-sm">{contentGroup.version}</div>
                </div>
            </Card>
        );
    };

    const renderScheduling = (contentGroup: ContentGroup) => {
        return (
            <Card className="px-8 py-4">
                <div className="flex justify-between text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faCalendarDays} className="mx-4"/>
                        Scheduling
                    </span>
                    <button onClick={() => setOpenOfferWindowsEditModal(true)}>
                        <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                    </button>
                </div>
                <div className="flex flex-row gap-8 justify-start mb-4">
                    <div className="flex flex-col gap-1 flex-wrap">
                        <label className="font-bold">License Window Start</label>
                        <span className="text-sm">
                        {
                            contentGroup.licensingWindow?.startDateTime
                            ? dayjs(contentGroup.licensingWindow.startDateTime).format("DD MMM YYYY [@] HH:mm")
                            : ""
                        }
                        </span>
                    </div>
                    <div className="flex flex-col gap-1 flex-wrap">
                        <label className="font-bold">License Window End</label>
                        <span className="text-sm">
                        {
                            contentGroup.licensingWindow?.endDateTime
                            ? dayjs(contentGroup.licensingWindow.endDateTime).format("DD MMM YYYY [@] HH:mm")
                            : ""
                        }
                        </span>
                    </div>
                </div>
                <OfferWindowsForm
                    openModal={openOfferWindowsEditModal}
                    onClose={() => setOpenOfferWindowsEditModal(false)}
                    contentGroup={contentGroup}
                />
            </Card>
        );
    };

    const renderImage = (image?: Image | null, deviceLabel?: string | null) => {
        return (
            <>
                <DashboardImage
                    description={""}
                    src={`/api/images/?url=${encodeURIComponent(image?.url ?? "")}`}
                />
                <div className="text-xs text-center mt-2">
                {
                    <span>
                    {
                        `${!!image?.size && !!ImageSizeLabels[image.size] && ImageSizeLabels[image.size]} ` +
                        `${!!image?.orientation && !!ImageOrientationLabels[image.orientation] && ImageOrientationLabels[image.orientation]}`
                    }
                    </span>
                }
                {
                    <span className="mx-2">|</span>
                }
                {
                    image?.purpose && ImagePurposeLabels[image.purpose] &&
                    <span>{`${deviceLabel ?? ""} ${ImagePurposeLabels[image.purpose]}`}</span>
                }
                </div>
                <div className="text-xs text-center mt-2">
                    {`${image?.width ?? "?"}px x ${image?.height ?? "?"}px`}
                </div>
            </>
        );
    };
    const renderImages = (contentGroup: ContentGroup) => {
        return (
            <Card className="px-8 py-4">
                <h2 className="text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faImages} className="mx-4"/>
                        Images
                    </span>
                </h2>
                <div className="grid grid-cols-5 grid-rows-[auto] items-center gap-4">
                    <div className="col-span-5 flex gap-2 items-center">
                        <label className="font-bold">Landscape Posters</label>
                        <FileUpload
                            asset={contentGroup}
                            imageEntityType={"POSTER_LANDSCAPE"}
                        />
                    </div>
                    <div className="row-start-2">
                        {renderImage(contentGroup?.images?.POSTER_LANDSCAPE_STANDARD_HERO_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(contentGroup?.images?.POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(contentGroup?.images?.POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(contentGroup?.images?.POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM, "High-Res")}
                    </div>
                    <div className="row-start-2">
                        {renderImage(contentGroup?.images?.POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE, "High-Res")}
                    </div>

                    <div className="col-span-5 flex gap-2 items-center">
                        <label className="font-bold">Landscape Clear Posters</label>
                        <FileUpload
                            asset={contentGroup}
                            imageEntityType={"CLEAR_POSTER_LANDSCAPE"}
                        />
                    </div>
                    <div className="row-start-4">
                        {renderImage(contentGroup?.images?.CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(contentGroup?.images?.CLEAR_POSTER_LANDSCAPE_STANDARD_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(contentGroup?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(contentGroup?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_THUMBNAIL_MEDIUM, "High-Res")}
                    </div>
                    <div className="row-start-4">
                        {renderImage(contentGroup?.images?.CLEAR_POSTER_LANDSCAPE_WIDESCREEN_HERO_LARGE, "High-Res")}
                    </div>
                    <div className="col-span-3 flex gap-2 items-center">
                        <label className="font-bold">Portrait Posters</label>
                        <FileUpload
                            asset={contentGroup}
                            imageEntityType={"POSTER_PORTRAIT"}
                        />
                    </div>
                    <div className="col-span-2 col-start-4 row-start-5"><label className="font-bold"></label></div>
                    <div className="row-start-6">
                        {renderImage(contentGroup?.images?.POSTER_PORTRAIT_THUMBNAIL_SMALL, "Low-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(contentGroup?.images?.POSTER_PORTRAIT_THUMBNAIL_MEDIUM, "Low-Res")}
                    </div>
                    <div className="row-start-6">
                        {renderImage(contentGroup?.images?.POSTER_PORTRAIT_THUMBNAIL_LARGE, "High-Res")}
                    </div>
                    <div className="row-start-6">
                    </div>
                    <div className="row-start-6">
                    </div>
                </div>
            </Card>
        );
    };

    const renderVideoType = (videoType?: VideoType | null) => {
        switch(videoType) {
            case "UNKNOWN": return "Default (High Definition)";
            case "SD": return "Standard Definition";
            case "HD": return "High Definition";
            case "THREE_D": return "3D";
            case "UHD4K": return "Ultra High Definition (4K)";
            default: return <></>;
        }
    };

    const renderAudioType = (audioType?: AudioType | null) => {
        switch(audioType) {
            case "UNKNOWN": return "Default (Stereo)";
            case "MONO": return "Mono";
            case "STEREO": return "Stereo";
            case "SURROUND_SOUND": return "Surround Sound";
            case "DOLBY_DIGITAL_5_1": return "Dolby Digital 5.1";
            default: return <></>;
        }
    };

    const renderPreviewVideoAsset = (previewVideoAsset: PreviewVideoAsset) => {
        return (
            <div className="flex flex-col gap-4 mb-4 overflow-x-hidden" key={previewVideoAsset.url}>
                <div className="text-sm text-nowrap text-ellipsis overflow-x-hidden">{previewVideoAsset.url ?? ""}</div>
                <div className="flex flex-row justify-between text-xs">
                    <div>{renderVideoType(previewVideoAsset.videoType)}</div>
                    <div>{renderAudioType(previewVideoAsset.audioType)}</div>
                    <div>{previewVideoAsset.runTime && badge(dayjs.duration(previewVideoAsset.runTime).format("HH:mm:ss"))}</div>
                </div>
            </div>
        );
    };

    const renderPreviewVideoAssets = (contentGroup: ContentGroup) => {
        if (!contentGroup.previewVideoAssets?.length) return (<></>);

        return (
            <Card className="px-8 py-4">
                <div className=" flex justify-between text-lg pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                    <span className="font-bold mr-6">
                        <FontAwesomeIcon icon={faFilm} className="mx-4"/>
                        Preview Video Assets
                    </span>
                    {
                        contentGroup.previewVideoAssets && contentGroup.previewVideoAssets.length > 0 &&
                        <>
                            <button
                                data-tooltip-content="Edit Preview Video Assets"
                                data-tooltip-id="global-tooltip"
                                onClick={() => setPreviewVideoAssetEditModal(true)}
                            >
                                <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300"/>
                            </button>
                            <PreviewVideoAssetFormModal
                                modalId={`previewVideoAssetModal-${contentGroup.id}`}
                                openModal={openPreviewVideoAssetEditModal}
                                onClose={() => setPreviewVideoAssetEditModal(false)}
                                contentGroup={contentGroup}
                            />
                        </>
                    }
                </div>

                {
                contentGroup.previewVideoAssets
                ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                ?.map(previewVideoAsset => renderPreviewVideoAsset(previewVideoAsset))}
            </Card>
        );
    };

    const renderVODContentSubGroupTable =
        (
            contentSubGroups: ContentGroup[],
            seriesContentGroup: ContentGroup | undefined,
            boxSetContentGroup: ContentGroup | undefined,
        ) => {
            return (
                <div className="mb-8">
                    <VODContentSubGroupTable
                        items={contentSubGroups}
                        seriesContentGroup={seriesContentGroup}
                        boxSetContentGroup={boxSetContentGroup}
                    />
                </div>
            );
        };

    const renderVODContentTable =
        (
            content: Content[],
            seasonContentGroup: ContentGroup | undefined,
            seriesContentGroup: ContentGroup | undefined,
            boxSetContentGroup: ContentGroup | undefined,
        ) => {
            return (
                <div className="mb-8">
                    <VODContentGroupContentTable
                        items={content}
                        seasonContentGroup={seasonContentGroup}
                        seriesContentGroup={seriesContentGroup}
                        boxSetContentGroup={boxSetContentGroup}
                    />
                </div>
            );
        };

    return contentGroup
     ? (
        <>
            <VODAdminLayout
                heading={`${contentGroup.contentGroupType ? ContentGroupTypeLabels[contentGroup.contentGroupType] : "VOD"} Content Group`}
                subHeading={
                    <div className="mt-4">
                        {renderNavigation(boxSetContentGroup, seriesContentGroup, seasonContentGroup)}
                    </div>
                }
            >
                <div className="flex flex-col mr-6">
                    <div className="grid grid-cols-12 grid-rows-[auto] gap-4 mb-10">
                        <div className="col-span-8 row-start-2">
                            {renderMainMetadata(seriesContentGroup, seasonContentGroup)}
                        </div>
                        <div className="col-span-4 col-start-9 row-start-2">
                            {renderReferences(contentGroup)}
                        </div>
                        <div className="col-span-12 row-start-3">
                            {renderScheduling(contentGroup)}
                        </div>
                        <div className="col-span-12 row-start-4">
                            {renderImages(contentGroup)}
                        </div>
                        <div className="col-span-4 row-start-5">
                            {renderPreviewVideoAssets(contentGroup)}
                        </div>
                        <div className="col-span-4 col-start-5 row-start-5">
                        </div>
                        <div className="col-span-4 col-start-9 row-start-5">
                        </div>
                    </div>
                    <div className="mb-10">
                        <h2 className="text-xl pb-2 mb-4 border-b-2 border-b-brand-500 dark:border-b-white">
                            <span className="font-bold mr-6">
                                Group Content
                            </span>
                        </h2>
                        { contentSubGroups?.length ? renderVODContentSubGroupTable(contentSubGroups, seriesContentGroup, boxSetContentGroup) : <></> }
                        { contentGroupContent?.length ? renderVODContentTable(contentGroupContent, seasonContentGroup, seriesContentGroup, boxSetContentGroup) : <></> }
                    </div>
                    <div className="p-10"></div>
                </div>
            </VODAdminLayout>
        </>
    ) : (
        <></>
    );
};

export default VODContentGroupView;